/**
 * Made In Canada Content
 *
 * Canadian brand story in homepage-style open layout.
 */

'use client'

import { cn } from '@/lib/utils'
import Link from 'next/link'
import { ArrowRightIcon } from '@radix-ui/react-icons'
import {
  IconCompass,
  IconLanguage,
  IconLock,
  IconMapPin
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'

import {
  MarketingContainer,
  MarketingPage,
  MarketingRailIcon
} from '@/components/marketing/marketing-primitives'
import { GeneratedMarketingAsset } from '@/components/marketing/generated-marketing-asset'
import {
  MarketingAssetOuterFade,
  marketingAssetSoftEdgeClassName
} from '@/components/marketing/marketing-asset-outer-fade'
import { CultTextureButton } from '@/components/marketing/cult-ui-components'
import { getMarketingCopy } from '@/lib/marketing-copy'

const principleIcons = [IconMapPin, IconLanguage, IconLock, IconCompass] as const

export default function MadeInCanadaContent() {
  const { i18n } = useTranslation()
  const copy = getMarketingCopy(i18n.resolvedLanguage || i18n.language)

  return (
    <MarketingPage>
      <MarketingContainer>
        <section className="relative grid items-center gap-10 py-6 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14 lg:py-10">
          <div className="flex max-w-[40rem] flex-col gap-5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.canada.eyebrow}
            </p>
            <h1 className="font-display text-balance text-5xl font-medium leading-[0.96] tracking-[-0.02em] text-slate-950 sm:text-6xl dark:text-slate-50">
              {copy.canada.title}
            </h1>
            <p className="text-base leading-8 text-slate-600 sm:text-lg dark:text-slate-300">
              {copy.canada.description}
            </p>
            <div className="flex flex-col gap-3 pt-1 sm:flex-row sm:flex-wrap">
              <CultTextureButton asChild>
                <Link href="/login?register=true">
                  {copy.common.startFree}
                  <ArrowRightIcon className="ml-2 size-4" />
                </Link>
              </CultTextureButton>
              <CultTextureButton asChild tone="light">
                <Link href="/features">{copy.common.explorePlatform}</Link>
              </CultTextureButton>
            </div>
          </div>
          <div className={cn("relative isolate min-h-[260px] overflow-hidden sm:min-h-[320px] lg:min-h-[380px]", marketingAssetSoftEdgeClassName)}>
            <GeneratedMarketingAsset
              assetKey="canadaContour"
              frame={false}
              presentation="open"
              className="absolute inset-0"
              imageClassName="object-contain object-center opacity-95"
              sizes="(min-width: 1024px) 560px, 100vw"
            />
            <MarketingAssetOuterFade />
          </div>
        </section>

        <section className="relative mt-10 py-12 lg:mt-14 lg:py-16">
          <div className="mx-auto max-w-[46rem] text-center">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.canada.principles.eyebrow}
            </p>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.canada.principles.title}
            </h2>
          </div>
          <div className="mt-12 grid gap-0 border-y border-slate-200/80 dark:border-white/10 sm:grid-cols-2">
            {copy.canada.principles.cards.map((card, index) => {
              const Icon = principleIcons[index] || principleIcons[0]

              return (
                <article
                  key={card.title}
                  className={`flex flex-col gap-3 px-5 py-8 ${
                    index % 2 === 1
                      ? 'sm:border-l sm:border-slate-200/80 dark:sm:border-white/10'
                      : ''
                  } ${
                    index >= 2
                      ? 'border-t border-slate-200/80 dark:border-white/10'
                      : index > 0
                        ? 'border-t border-slate-200/80 sm:border-t-0 dark:border-white/10'
                        : ''
                  }`}
                >
                  <MarketingRailIcon icon={Icon} />
                  <h3 className="text-xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
                    {card.title}
                  </h3>
                  <p className="text-sm leading-7 text-slate-600 dark:text-slate-300">
                    {card.description}
                  </p>
                </article>
              )
            })}
          </div>
        </section>

        <section className="relative mt-4 py-12 lg:mt-8 lg:py-16">
          <div className="mx-auto max-w-[40rem]">
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              {copy.canada.language.eyebrow}
            </p>
            <h2 className="font-display mt-4 text-4xl font-medium leading-[0.98] tracking-[-0.02em] text-slate-950 sm:text-5xl dark:text-slate-50">
              {copy.canada.language.title}
            </h2>
            <p className="mt-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-400">
              {copy.canada.language.label}
            </p>
            <h3 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950 dark:text-slate-50">
              {copy.canada.language.panelTitle}
            </h3>
            <p className="mt-4 text-base leading-8 text-slate-600 dark:text-slate-300">
              {copy.canada.language.panelDescription}
            </p>
            <p className="mt-6 text-sm leading-7 text-slate-500 dark:text-slate-400">
              Cree · Ojibwe · Cherokee · Navajo · Inuktitut
            </p>
          </div>
        </section>
      </MarketingContainer>
    </MarketingPage>
  )
}
